// Menú lateral colapsable (escritorio), mismo diseño que la app principal.
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, BarChart3, ShoppingBag, TrendingUp, DollarSign, Wallet,
  Users, ClipboardList, Terminal, ChevronLeft, ChevronRight, LogOut,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

export const NAV_ITEMS = [
  { path: '/',                   label: 'Turnos',       icon: ClipboardList },
  { path: '/dashboard-new',      label: 'Dashboard',    icon: LayoutDashboard },
  { path: '/analytics',          label: 'Estadísticas', icon: BarChart3 },
  { path: '/products-analytics', label: 'Productos',    icon: ShoppingBag },
  { path: '/kpi-analytics',      label: 'Indicadores',  icon: TrendingUp },
  { path: '/product-costs',      label: 'Costos',       icon: DollarSign },
  { path: '/finanzas',           label: 'Finanzas',     icon: Wallet },
  { path: '/vendedores',         label: 'Vendedores',   icon: Users },
];

export const isNavActive = (path, pathname) => (path === '/' ? pathname === '/' : pathname.startsWith(path));

const readCollapsed = () => {
  try { return localStorage.getItem('sidebarCollapsed') === 'true'; } catch { return false; }
};

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout, navigateToTerminal } = useAuth();

  const toggle = () => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem('sidebarCollapsed', String(next)); } catch { /* sin storage */ }
      return next;
    });
  };

  const itemClass = (active) => cn(
    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm font-medium cursor-pointer',
    collapsed && 'justify-center',
    active
      ? 'bg-blue-600/20 text-blue-400 border border-blue-500/25'
      : 'text-white/50 hover:text-white hover:bg-white/5 border border-transparent'
  );

  return (
    <aside
      className={cn(
        'hidden md:flex flex-col shrink-0 h-screen sticky top-0 z-40',
        'bg-gray-950 border-r border-white/10',
        'transition-all duration-300 ease-in-out overflow-hidden',
        collapsed ? 'w-[68px]' : 'w-[224px]'
      )}
    >
      {/* Logo */}
      <div className={cn('flex items-center h-16 px-3 border-b border-white/10 shrink-0 gap-3', collapsed && 'justify-center')}>
        <img src="/LogoGreenpr.png" alt="Green Vape" className="h-9 w-9 shrink-0 object-contain" />
        {!collapsed && (
          <div className="min-w-0">
            <p className="text-white font-bold text-sm leading-tight truncate">Green Vape</p>
            <p className="text-white/40 text-xs leading-tight truncate">Personal & Ventas</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden space-y-0.5 px-2">
        {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
          <button
            key={path}
            type="button"
            onClick={() => navigate(path)}
            title={collapsed ? label : undefined}
            className={itemClass(isNavActive(path, pathname))}
          >
            <Icon className="h-[18px] w-[18px] shrink-0" />
            {!collapsed && <span className="truncate">{label}</span>}
          </button>
        ))}
      </nav>

      {/* Bottom */}
      <div className="border-t border-white/10 p-2 space-y-1 shrink-0">
        <button type="button" onClick={navigateToTerminal} title={collapsed ? 'Terminal de turnos' : undefined} className={itemClass(false)}>
          <Terminal className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Terminal</span>}
        </button>
        <button
          type="button"
          onClick={logout}
          title={collapsed ? 'Cerrar sesión' : undefined}
          className={cn(
            'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm cursor-pointer',
            'text-red-400/60 hover:text-red-400 hover:bg-red-500/10',
            collapsed && 'justify-center'
          )}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Cerrar sesión</span>}
        </button>
        <button
          type="button"
          onClick={toggle}
          title={collapsed ? 'Expandir' : 'Colapsar'}
          className={cn(
            'w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-xs cursor-pointer',
            'text-white/30 hover:text-white/60 hover:bg-white/5',
            collapsed && 'justify-center'
          )}
        >
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : (<><ChevronLeft className="h-3.5 w-3.5" /><span>Colapsar</span></>)}
        </button>
      </div>
    </aside>
  );
}
